/**
 * Typing a date, and the slashes.
 *
 * A date field that makes you type `/` is a date field people get wrong: they
 * type `09172026`, or `9/17/26`, or paste `2026-09-17` out of a spreadsheet.
 * So the field takes digits and puts the slashes in itself, and throws away
 * any separator you type — which means typing the slashes is also fine, and
 * neither habit is punished.
 *
 * Kept away from the component because the interesting part is the caret. A
 * mask that reformats on every keystroke and then drops the caret at the end
 * is a mask you cannot edit the month of, and that is a thing you only find by
 * trying it — or by a test that says where the caret went.
 */

/** What the field shows while it is empty, and the shape it is asking for. */
export const DATE_MASK = 'mm/dd/yyyy'

export interface Masked {
  text: string
  /** Where the caret belongs afterwards. */
  caret: number
}

/**
 * The digits of `text`, and how many of them are before `caret`.
 *
 * The count is what survives reformatting: character positions move when a
 * slash is inserted, digit positions do not.
 */
function digitsBefore(text: string, caret: number): number {
  let count = 0
  for (let i = 0; i < caret && i < text.length; i += 1) {
    if (text[i]! >= '0' && text[i]! <= '9') count += 1
  }
  return count
}

/** `09172026` → `09/17/2026`. Anything past eight digits is dropped. */
export function formatDigits(digits: string): string {
  const d = digits.slice(0, 8)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

/** Where in the formatted text the caret sits after `n` digits. */
function caretAfter(digits: number): number {
  if (digits <= 2) return digits
  if (digits <= 4) return digits + 1
  return digits + 2
}

/**
 * Reformats what is now in the box, keeping the caret against the same digit.
 *
 * `previous` is what the box held a keystroke ago, and it is here for one
 * case: backspacing over a slash. The browser removes the `/` from `09/|17`
 * and hands back `0917`, which has the same digits as before — so re-masking
 * it puts the slash straight back and the key appears to have done nothing.
 * People then hold it down and lose the month. When the text got shorter but
 * the digits did not, the separator was what went, so this takes the digit in
 * front of it instead.
 */
export function maskDate(raw: string, caret: number, previous = ''): Masked {
  const typed = raw.replace(/\D/g, '')
  const had = previous.replace(/\D/g, '')
  let before = digitsBefore(raw, caret)

  let digits = typed
  if (raw.length < previous.length && typed.length === had.length && before > 0) {
    digits = typed.slice(0, before - 1) + typed.slice(before)
    before -= 1
  }
  digits = digits.slice(0, 8)

  return { text: formatDigits(digits), caret: caretAfter(Math.min(before, digits.length)) }
}

/**
 * What a filled-in field means, as `yyyy-mm-dd` — or null while it is still
 * being typed.
 *
 * Strict about real dates: `02/31/2026` is eight digits and not a day, and a
 * filter built on it would quietly match nothing.
 */
export function parseMasked(text: string): string | null {
  const digits = text.replace(/\D/g, '')
  if (digits.length !== 8) return null

  const month = Number(digits.slice(0, 2))
  const day = Number(digits.slice(2, 4))
  const year = Number(digits.slice(4))
  if (month < 1 || month > 12 || day < 1 || year < 1000) return null

  // Round-tripping through Date catches the 31st of February without a table
  // of month lengths and a leap-year rule of its own.
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null

  return `${String(year).padStart(4, '0')}-${digits.slice(0, 2)}-${digits.slice(2, 4)}`
}

/** `2026-09-17` → `09/17/2026`, for putting a stored value into the box. */
export function maskFromIso(iso: string | null | undefined): string {
  if (!iso) return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return ''
  return `${match[2]}/${match[3]}/${match[1]}`
}

/**
 * Finishing a half-typed year when the field is left.
 *
 * `9/17/26` is how people write dates and how every spreadsheet accepts them.
 * Only on blur, never while typing: expanding as the second digit lands would
 * fight somebody halfway through `2026`.
 */
export function completeOnBlur(text: string): string {
  const digits = text.replace(/\D/g, '')
  if (digits.length !== 6) return text
  const year = Number(digits.slice(4))
  // The same pivot Postgres and Excel use: 69 and below is this century.
  const full = year <= 68 ? 2000 + year : 1900 + year
  return formatDigits(`${digits.slice(0, 4)}${full}`)
}
